import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useOrganization } from '../../context/OrganizationContext';
import PastDueWarningBanner from '../../components/PastDueWarningBanner';

/**
 * SubscriptionGuard - Blocks access when subscription is not valid
 *
 * Grace Period (past_due):
 * - Owners: See warning banner, can continue using app
 * - Non-owners: Can continue using app normally
 *
 * Blocked Statuses (canceled, incomplete, incomplete_expired, unpaid):
 * - Owners: Redirected to billing page
 * - Non-owners: Show locked account message
 */
const SubscriptionGuard = ({ children }) => {
  const location = useLocation();
  const { currentOrganization, userRole } = useOrganization();

  // Only apply guard if user is in organization mode
  if (currentOrganization) {
    const subscriptionStatus = currentOrganization?.subscription?.status;
    const isOwner = userRole === 'owner';

    // Allowed subscription statuses (includes past_due for grace period)
    const isValidSubscription =
      subscriptionStatus === 'active' ||
      subscriptionStatus === 'trialing' ||
      subscriptionStatus === 'past_due'; // Grace period - allow access with warning

    if (!isValidSubscription) {
      // Blocked statuses: canceled, incomplete, incomplete_expired, unpaid

      // For owners: redirect to billing page
      if (isOwner) {
        const billingPath = '/settings/organization';
        const isOnBillingPage = location.pathname === billingPath && location.search.includes('tab=billing');

        // Don't redirect if already on billing page to avoid infinite loop
        if (!isOnBillingPage) {
          return (
            <Navigate
              to="/settings/organization?tab=billing"
              state={{
                from: location,
                message: 'Please activate your subscription to continue using the application.'
              }}
              replace
            />
          );
        }
      } else {
        // For non-owners: show locked account message
        return (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '20px',
            textAlign: 'center',
            backgroundColor: '#f5f5f5'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '40px',
              maxWidth: '500px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}>
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ff6b6b"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ margin: '0 auto 20px' }}
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
              <h2 style={{ fontSize: '24px', marginBottom: '10px', color: '#333' }}>
                Account Locked
              </h2>
              <p style={{ fontSize: '16px', color: '#666' }}>
                This organization's subscription is not active. Please contact your account owner to reactivate the account.
              </p>
            </div>
          </div>
        );
      }
    }

    // If status is past_due, show warning banner for owners
    if (subscriptionStatus === 'past_due') {
      return (
        <>
          <PastDueWarningBanner subscriptionStatus={subscriptionStatus} userRole={userRole} />
          {children}
        </>
      );
    }
  }

  // Allow access for:
  // - Individual mode users
  // - Organization members with active, trialing, or past_due subscriptions
  return children;
};

export default SubscriptionGuard;
